import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import {
  Phone,
  ChevronDown,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { fetchCalls, fetchClientSummary } from '../api';
import CallDetailPanel from './calls/CallDetailPanel';
import { formatTopicLabel } from '../utils/callClassification';
import { formatCallDirection, formatNegativeSeverity } from '../utils/labels';
import { formatNegativeCategory, formatNegativeSubCategory } from '../utils/negativeLabels';

const moscowDateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : moscowDateTimeFormatter.format(date);
};

const toDateParam = (value) => (value ? format(value, 'yyyy-MM-dd') : undefined);

const ClientDetailsModal = ({ client, dateRange, onClose }) => {
  const open = Boolean(client);
  const clientId = client?.clientId;

  const params = useMemo(
    () => ({ date_from: toDateParam(dateRange?.from), date_to: toDateParam(dateRange?.to) }),
    [dateRange?.from, dateRange?.to]
  );

  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [expandedCalls, setExpandedCalls] = useState(() => new Set());

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadCalls = useCallback(async () => {
    if (!clientId) return;
    setCallsLoading(true);
    try {
      const response = await fetchCalls({ client_id: clientId, ...params, limit: 200, offset: 0 });
      setCalls(response.items ?? []);
    } catch (err) {
      console.error(err);
      setCalls([]);
    } finally {
      setCallsLoading(false);
    }
  }, [clientId, params]);

  const loadSummary = useCallback(async () => {
    if (!clientId) return;
    setSummaryLoading(true);
    try {
      const response = await fetchClientSummary(clientId, params);
      setSummary(response);
    } catch (err) {
      console.error(err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [clientId, params]);

  useEffect(() => {
    if (!open) return;
    setExpandedCalls(new Set());
    loadCalls();
    loadSummary();
  }, [open, loadCalls, loadSummary]);

  const toggleCall = useCallback((callId) => {
    setExpandedCalls((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) {
        next.delete(callId);
      } else {
        next.add(callId);
      }
      return next;
    });
  }, []);

  const clientTitle = client?.name || client?.company || client?.phone || 'Клиент';

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent trackId="clients.details" className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{clientTitle}</DialogTitle>
          <DialogDescription>
            {client?.phone || '—'}
            {client?.company ? ` · ${client.company}` : ''}
          </DialogDescription>
        </DialogHeader>

        <Tabs trackId="clients.details.tabs" defaultValue="calls" className="w-full">
          <TabsList>
            <TabsTrigger value="calls">Звонки</TabsTrigger>
            <TabsTrigger value="summary">Сводка</TabsTrigger>
          </TabsList>

          <TabsContent value="calls">
            <CallsTab
              calls={calls}
              loading={callsLoading}
              expandedCalls={expandedCalls}
              onToggle={toggleCall}
            />
          </TabsContent>

          <TabsContent value="summary">
            <SummaryTab loading={summaryLoading} summary={summary} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const CallsTab = ({ calls, loading, expandedCalls, onToggle }) => {
  if (loading) {
    return <div className="px-1 py-4 text-sm text-slate-500">Загрузка звонков...</div>;
  }

  if (calls.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        Нет звонков за выбранный период.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead className="max-md:hidden">Тип</TableHead>
            <TableHead>Тема / классификация</TableHead>
            <TableHead className="max-md:hidden">Оператор</TableHead>
            <TableHead>Оценка жалобы</TableHead>
            <TableHead className="max-md:hidden">Тип жалобы</TableHead>
            <TableHead className="max-md:hidden">Подтип</TableHead>
            <TableHead>Дата и время</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.map((call) => {
            const isExpanded = expandedCalls.has(call.callId);
            const Icon = isExpanded ? ChevronDown : ChevronRight;
            const hasNegative = Boolean(call.negativeSeverity) || (call.negativeCategories?.length ?? 0) > 0;

            return (
              <React.Fragment key={call.callId}>
                <TableRow className="cursor-pointer" data-ev="clients.details.call_toggle" onClick={() => onToggle(call.callId)}>
                  <TableCell><Icon className="h-4 w-4 text-slate-500" /></TableCell>
                  <TableCell className="max-md:hidden">
                    <div className="inline-flex items-center gap-2">
                      <Phone className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm text-slate-700">{formatCallDirection(call.callType)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-700">{formatTopicLabel(call.topic, call.callClassificationType)}</div>
                  </TableCell>
                  <TableCell className="max-md:hidden"><span className="text-sm text-slate-700">{call.employeeName || '—'}</span></TableCell>
                  <TableCell>
                    {hasNegative ? (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                        {formatNegativeSeverity(call.negativeSeverity) || 'есть'}
                      </Badge>
                    ) : (
                      <span className="text-sm text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="max-md:hidden">
                    {(call.negativeCategories?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {call.negativeCategories.map((category, idx) => (
                          <Badge key={idx} variant="outline" className="border-red-200 bg-red-50 text-xs text-red-700">
                            {formatNegativeCategory(category)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="max-md:hidden">
                    {(call.negativeSubCategories?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {call.negativeSubCategories.map((subCategory, idx) => (
                          <Badge key={idx} variant="outline" className="border-amber-200 bg-amber-50 text-xs text-amber-700">
                            {formatNegativeSubCategory(subCategory)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(call.callStartedAt)}</TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-slate-50/70 p-0">
                      <div className="px-4 py-4 max-sm:sticky max-sm:left-0 max-sm:max-w-[calc(100vw-3rem)] max-sm:px-2">
                        <CallDetailPanel call={call} />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const SummaryTab = ({ loading, summary }) => {
  if (loading) {
    return <div className="px-1 py-4 text-sm text-slate-500">Загрузка сводки...</div>;
  }

  if (!summary) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        Нет данных по клиенту.
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SummaryBlock title="За выбранный период" block={summary.period} />
      <SummaryBlock title="За всё время" block={summary.lifetime} />
    </div>
  );
};

const SummaryBlock = ({ title, block }) => {
  const data = block ?? {};
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="grid grid-cols-2 gap-3">
        <SummaryMetric
          icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
          label="Жалоб"
          value={data.complaintsCount ?? 0}
          danger={(data.complaintsCount ?? 0) > 0}
        />
        <SummaryMetric
          icon={<Phone className="h-4 w-4 text-slate-500" />}
          label="Звонков"
          value={data.callsCount ?? 0}
        />
      </div>
    </div>
  );
};

const SummaryMetric = ({ icon, label, value, danger = false }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
    <div className="flex items-center gap-2 text-xs text-slate-500">
      {icon}
      {label}
    </div>
    <div className={`mt-1 text-lg font-semibold ${danger ? 'text-red-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

export default ClientDetailsModal;
